import { call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import { createAction } from '@reduxjs/toolkit';
import { loginSelector } from '../userFarmSlice';
import { axios, getHeader } from '../saga';
import i18n from '../../locales/i18n';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../Snackbar/snackbarSlice';
import {
  onLoadingOrthophotoStart,
  onLoadingOrthophotoFail,
  getOrthophotosSuccess,
  addOrthophotoSuccess,
  updateOrthophotoSuccess,
  deleteOrthophotoSuccess,
} from '../orthophotoSlice';
import { url } from '../../apiConfig';

const orthophotoUrl = `${url}/orthophoto`;

export const fetchOrthophotosByFarm = createAction('fetchOrthophotosByFarmSaga');

export function* fetchOrthophotosByFarmSaga() {
  const { user_id, farm_id } = yield select(loginSelector);
  const header = getHeader(user_id, farm_id);
  try {
    yield put(onLoadingOrthophotoStart());
    const result = yield call(axios.get, `${orthophotoUrl}/farm/${farm_id}`, header);
    yield put(getOrthophotosSuccess(result.data));
  } catch (e) {
    yield put(onLoadingOrthophotoFail());
    console.error('Failed to fetch orthophotos', e);
  }
}

export const uploadOrthophoto = createAction('uploadOrthophotoSaga');

export function* uploadOrthophotoSaga({ payload }) {
  const { file, location_id, task_id, capture_date, onProgress } = payload;
  const { user_id, farm_id } = yield select(loginSelector);
  const header = getHeader(user_id, farm_id);

  const formData = new FormData();
  formData.append('file', file);
  formData.append('location_id', location_id);
  formData.append('task_id', String(task_id));
  if (capture_date) {
    formData.append('capture_date', capture_date);
  }

  try {
    const result = yield call(axios.post, `${orthophotoUrl}/upload/farm/${farm_id}`, formData, {
      ...header,
      headers: {
        ...header.headers,
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress
        ? (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        : undefined,
    });
    yield put(addOrthophotoSuccess(result.data));
    yield put(enqueueSuccessSnackbar(i18n.t('message:ORTHOPHOTO.UPLOAD.SUCCESS')));
  } catch (e) {
    console.error('Orthophoto upload failed', e);
    yield put(enqueueErrorSnackbar(i18n.t('message:ORTHOPHOTO.UPLOAD.FAILED')));
  }
}

export const toggleOrthophotoVisibility = createAction('toggleOrthophotoVisibilitySaga');

export function* toggleOrthophotoVisibilitySaga({ payload }) {
  const { orthophoto_id, visible } = payload;
  const { user_id, farm_id } = yield select(loginSelector);
  const header = getHeader(user_id, farm_id);

  try {
    yield call(axios.patch, `${orthophotoUrl}/${orthophoto_id}`, { visible }, header);
    yield put(
      updateOrthophotoSuccess({
        id: orthophoto_id,
        changes: { visible },
      }),
    );
  } catch (e) {
    console.error('Failed to toggle orthophoto visibility', e);
    yield put(enqueueErrorSnackbar(i18n.t('message:ORTHOPHOTO.UPDATE.FAILED')));
  }
}

export const updateOrthophotoOpacity = createAction('updateOrthophotoOpacitySaga');

export function* updateOrthophotoOpacitySaga({ payload }) {
  const { orthophoto_id, opacity } = payload;
  const { user_id, farm_id } = yield select(loginSelector);
  const header = getHeader(user_id, farm_id);

  try {
    yield call(axios.patch, `${orthophotoUrl}/${orthophoto_id}`, { opacity }, header);
    yield put(
      updateOrthophotoSuccess({
        id: orthophoto_id,
        changes: { opacity },
      }),
    );
  } catch (e) {
    console.error('Failed to update orthophoto opacity', e);
  }
}

export const deleteOrthophoto = createAction('deleteOrthophotoSaga');

export function* deleteOrthophotoSaga({ payload }) {
  const { orthophoto_id } = payload;
  const { user_id, farm_id } = yield select(loginSelector);
  const header = getHeader(user_id, farm_id);

  try {
    yield call(axios.delete, `${orthophotoUrl}/${orthophoto_id}`, header);
    yield put(deleteOrthophotoSuccess(orthophoto_id));
    yield put(enqueueSuccessSnackbar(i18n.t('message:ORTHOPHOTO.DELETE.SUCCESS')));
  } catch (e) {
    console.error('Failed to delete orthophoto', e);
    yield put(enqueueErrorSnackbar(i18n.t('message:ORTHOPHOTO.DELETE.FAILED')));
  }
}

export default function* orthophotoSaga() {
  yield takeLatest(fetchOrthophotosByFarm.type, fetchOrthophotosByFarmSaga);
  yield takeLeading(uploadOrthophoto.type, uploadOrthophotoSaga);
  yield takeLeading(toggleOrthophotoVisibility.type, toggleOrthophotoVisibilitySaga);
  yield takeLeading(updateOrthophotoOpacity.type, updateOrthophotoOpacitySaga);
  yield takeLeading(deleteOrthophoto.type, deleteOrthophotoSaga);
}
